import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, Search, RefreshCw } from "lucide-react";

const DEVICE_TYPES = ["phone", "tablet", "laptop", "watch"];
const SERVICES = ["screen", "battery", "multi_component", "diagnostics", "cleaning"];

const emptyRow = { device_type: "phone", brand: "", model_name: "", service: "screen", parts_cost: "", parts_name: "" };

export default function AdminPricing() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("all");
  const [draft, setDraft] = useState(emptyRow);
  const [saving, setSaving] = useState(false);
  const [reseeding, setReseeding] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/parts-prices");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (deviceFilter !== "all" && r.device_type !== deviceFilter) return false;
      if (!needle) return true;
      return (
        r.brand.toLowerCase().includes(needle) ||
        r.model_name.toLowerCase().includes(needle) ||
        r.service.toLowerCase().includes(needle) ||
        (r.parts_name || "").toLowerCase().includes(needle)
      );
    });
  }, [rows, q, deviceFilter]);

  const save = async () => {
    if (!draft.brand.trim() || !draft.model_name.trim() || !draft.parts_cost || !draft.parts_name.trim()) {
      toast.error("Fill every field before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/parts-prices", {
        ...draft,
        parts_cost: parseFloat(draft.parts_cost),
      });
      toast.success("Price saved");
      setDraft(emptyRow);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this price? Bookings for this model will fall back to the flat estimate.")) return;
    try {
      await api.delete(`/admin/parts-prices/${id}`);
      toast.success("Price removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const inlineUpdate = async (row, patch) => {
    try {
      await api.post("/admin/parts-prices", { ...row, ...patch });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const reseed = async () => {
    if (!window.confirm("Refresh all curated Amazon prices back to the built-in defaults? Any models you added or edited outside the curated catalog will be left alone.")) return;
    setReseeding(true);
    try {
      const { data } = await api.post("/admin/parts-prices/reseed");
      toast.success(`Refreshed ${data.reseeded} curated prices (${data.updated} updated, ${data.added} added). ${data.admin_rows_preserved} of your custom rows preserved.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setReseeding(false); }
  };

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
        <div>
          <Link to="/admin" data-testid="pricing-back" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to admin
          </Link>
          <div className="overline mt-2">Parts pricing</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Amazon parts catalog</h1>
          <p className="mt-2 text-slate-600">Set the parts cost per device model. Quotes automatically use these prices when the customer's model matches; otherwise they fall back to the flat estimate.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="reseed-prices-btn"
            onClick={reseed}
            disabled={reseeding}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold hover:border-slate-400 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${reseeding ? "animate-spin" : ""}`} /> {reseeding ? "Refreshing…" : "Refresh Amazon prices"}
          </button>
          <div className="text-sm text-slate-500 font-mono" data-testid="pricing-count">{rows.length} entries</div>
        </div>
      </div>

      <div className="p-4 md:p-6 rounded-2xl border border-slate-200 bg-white mb-6">
        <div className="overline mb-3">Add or update a model</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-6 gap-3">
          <select
            data-testid="new-device-type"
            value={draft.device_type}
            onChange={(e) => setDraft({ ...draft, device_type: e.target.value })}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            {DEVICE_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <input data-testid="new-brand" value={draft.brand} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} placeholder="Brand (Apple)" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
          <input data-testid="new-model" value={draft.model_name} onChange={(e) => setDraft({ ...draft, model_name: e.target.value })} placeholder="Model (iPhone 15 Pro)" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
          <select
            data-testid="new-service"
            value={draft.service}
            onChange={(e) => setDraft({ ...draft, service: e.target.value })}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="new-cost" type="number" step="0.01" min="0" value={draft.parts_cost} onChange={(e) => setDraft({ ...draft, parts_cost: e.target.value })} placeholder="Amazon price" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-mono" />
          <button
            data-testid="save-price-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-500 disabled:opacity-40"
          >
            {saving ? "Saving…" : <><Plus className="w-4 h-4" /> Save</>}
          </button>
        </div>
        <input
          data-testid="new-parts-name"
          value={draft.parts_name}
          onChange={(e) => setDraft({ ...draft, parts_name: e.target.value })}
          placeholder="Parts name (e.g. iPhone 15 Pro OLED assembly)"
          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
        />
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="pricing-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search brand, model, service…"
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
          />
        </div>
        <select value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value)} data-testid="pricing-device-filter" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="all">All devices</option>
          {DEVICE_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="pricing-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">Device</th>
              <th className="p-3">Brand</th>
              <th className="p-3">Model</th>
              <th className="p-3">Service</th>
              <th className="p-3">Parts name</th>
              <th className="p-3">Cost</th>
              <th className="p-3">Source</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-500">No prices match your filter.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                <td className="p-3 text-slate-500 capitalize">{r.device_type}</td>
                <td className="p-3 font-medium text-slate-900">{r.brand}</td>
                <td className="p-3 text-slate-900">{r.model_name}</td>
                <td className="p-3 text-slate-500">{r.service}</td>
                <td className="p-3 text-slate-600 text-xs">{r.parts_name}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-400 font-mono">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={r.parts_cost}
                      data-testid={`price-input-${r.id}`}
                      className="w-24 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm font-mono"
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v !== r.parts_cost) inlineUpdate(r, { parts_cost: v });
                      }}
                    />
                  </div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full ${r.source === "admin_edit" || r.source === "admin_manual" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {r.source === "curated_seed" ? "Seed" : "You"}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button
                    data-testid={`delete-price-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
